import React, { useState } from "react";
import { z } from "zod";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FaKey, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { useConfirmPasswordReset } from "@/api/AuthApi";
import { handleApiErrors } from "@/utils/errorMapper";
import SmallLoader from "@/components/SmallLoader";
import { failedToast } from "@/utils/toasts";
import SupportModal from "@/components/modals/SupportModal";

const NewPasswordSchema = z
  .object({
    new_password: z
      .string()
      .min(8, "Le mot de passe doit comporter au moins 8 caractères"),
    confirm_password: z
      .string()
      .min(8, "Le mot de passe doit comporter au moins 8 caractères"),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirm_password"],
  });

type NewPasswordFormData = z.infer<typeof NewPasswordSchema>;

const CreateNewPassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const { uidb64, token } = useParams<{ uidb64: string; token: string }>();
  const { confirmPasswordReset, loading } = useConfirmPasswordReset();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(NewPasswordSchema),
  });

  const onSubmit: SubmitHandler<NewPasswordFormData> = async (data) => {
    if (!uidb64 || !token) {
      failedToast("Lien de réinitialisation invalide ou expiré.");
      return;
    }

    const result = await confirmPasswordReset(
      uidb64,
      token,
      data.new_password,
      data.confirm_password,
    );

    if (result.success) {
      navigate("/login/resetPassword/successPage");
    } else if (result.error) {
      handleApiErrors(
        result.error,
        setError,
        "Impossible de réinitialiser le mot de passe. Le lien est peut-être expiré.",
      );
      navigate("/login");
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
      <div className="absolute top-6 left-8 flex items-center space-x-2">
        <img
          src="/images/RAJAPI_logo.png"
          alt="Rajapi Logo"
          className="w-8 h-8"
        />
        <span className="text-xl font-extrabold text-blue-500">RAJAPI-COP</span>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-lg max-w-sm w-full relative">
        {loading && <SmallLoader modal={true} />}

        <div className="flex flex-col items-center">
          <div className="text-blue-500 text-4xl mb-4">
            <img src="/images/modiflogo.png" />
          </div>
          <h2 className="text-lg font-semibold">
            Créer un nouveau mot de passe
          </h2>
          <p className="text-sm text-gray-600 text-center mt-2">
            Saisissez un nouveau mot de passe et confirmez-le pour continuer.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mt-6">
            <label
              htmlFor="new_password"
              className="text-sm font-medium text-gray-500"
            >
              Nouveau mot de passe
            </label>
            <div className="relative mt-2">
              <div className="flex justify-between gap-2">
                <FaKey className=" text-gray-400 text-xl mt-2" />
                <input
                  id="new_password"
                  type={showPassword ? "text" : "password"}
                  placeholder="********"
                  {...register("new_password")}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 focus:outline-none"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            {errors.new_password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.new_password.message}
              </p>
            )}
          </div>

          <div className="mt-4">
            <label
              htmlFor="confirm_password"
              className="text-sm font-medium text-gray-500"
            >
              Confirmer le mot de passe
            </label>
            <div className="relative mt-2">
              <div className="flex justify-between gap-2">
                <FaKey className="text-gray-400 text-xl mt-2" />
                <input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="********"
                  {...register("confirm_password")}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-3 text-gray-400 focus:outline-none"
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="text-red-500 text-xs mt-1">
                {errors.confirm_password.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full mt-6 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg"
          >
            Confirmer
          </button>
        </form>

        <div className="mt-4">
          <SupportModal />
        </div>
      </div>

      <footer className="absolute bottom-4 text-gray-400 text-sm">
        © 2024 Kiosk
      </footer>
    </div>
  );
};

export default CreateNewPassword;
